import React, { useState, useRef, useEffect } from 'react';
import { copyClipboard } from '@/utils/copy-clipboard';
import { useMutation, ApolloError } from '@apollo/client';
import { API_DOCS_URL } from '@/constants';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Link,
    Stack,
    Box,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Skeleton,
    IconButton,
    Tooltip,
    Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RotateApiClientDocument } from '@/graphql/generated/rotateApiClient.generated';
import { ApiClientKeySlot } from '@/graphql/generated/types.generated';
import { PlanLimit } from '@/graphql/generated/types.generated';
import IntegrationUsageMeter, { IntegrationQuotaView } from '@/components/api/IntegrationUsageMeter';
import IntegrationUsageChart, { IntegrationUsageDayView } from '@/components/api/IntegrationUsageChart';
import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import { AutorenewRounded, ContentCopyRounded, InfoOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { toggleChat } from '@/utils/crisp-chat';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WorkspaceApiManagerProps {
    apiClient:
        | {
              id: string;
              lastKeySlotUsed?: ApiClientKeySlot | null;
              lastUsedAt?: string | null;
              primaryKey?: string | null;
              primaryKeyGeneratedAt?: string | null;
              secondaryKey?: string | null;
              secondaryKeyGeneratedAt?: string | null;
          }
        | null
        | undefined;
    loading: boolean;
    hasApiAccess: boolean;
    apiQuota?: IntegrationQuotaView | null;
    dailyUsage?: IntegrationUsageDayView[] | null;
    periodStartDay?: string | null;
    periodResetsAt?: string | null;
    usageLoading?: boolean;
    createdKeys: { primaryKey?: string | null; secondaryKey?: string | null } | null;
    rotatedKey: { slot: ApiClientKeySlot; key: string; generatedAt?: string } | null;
    creating: boolean;
    onCreateApiClient: () => void;
    onRotateSuccess: (slot: ApiClientKeySlot, key: string, generatedAt?: string) => void;
}

// ---------------------------------------------------------------------------
// ApiKeySection
// ---------------------------------------------------------------------------

const ApiKeySection: React.FC<{
    label: string;
    keyValue?: string;
    generatedAt?: string;
    keySlot: ApiClientKeySlot;
    onRegenerate: (slot: ApiClientKeySlot) => void;
}> = ({ label, keyValue, generatedAt, keySlot, onRegenerate }) => {
    const [copied, setCopied] = useState(false);
    const { t } = useTranslation();
    // 32-char placeholder for hidden key
    const keyPlaceholder = '•'.repeat(32);
    const showKey = Boolean(keyValue);

    const handleCopy = () => {
        copyClipboard(
            keyValue ?? '',
            () => {
                enqueueSnackbar(t('str.action.copied'));
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            },
            () => {
                enqueueSnackbar(t('error.something-wrong'));
            },
        );
    };
    return (
        <Box sx={{ flex: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1">{t(label)}</Typography>
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                    type={showKey ? 'text' : 'password'}
                    value={showKey ? keyValue : keyPlaceholder}
                    fullWidth
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <>
                                <Tooltip title={t('settings.workspace.api.key.regenerate')}>
                                    <IconButton
                                        onClick={() => onRegenerate(keySlot)}
                                        aria-label={t('settings.workspace.api.key.regenerate')}
                                        tabIndex={0}
                                        sx={{ minWidth: 0, px: 1 }}
                                        size="small"
                                    >
                                        <AutorenewRounded fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {showKey && (
                                    <Tooltip title={t('str.action.copy')}>
                                        <IconButton
                                            onClick={handleCopy}
                                            aria-label={t('str.action.copy')}
                                            tabIndex={0}
                                            sx={{ minWidth: 0, px: 1 }}
                                            color={copied ? 'info' : 'default'}
                                            size="small"
                                        >
                                            <ContentCopyRounded
                                                fontSize="small"
                                                color={copied ? 'info' : 'inherit'}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </>
                        ),
                    }}
                    inputProps={{ 'aria-label': t(label) }}
                    helperText={
                        <>
                            <Typography variant="caption" color="text.secondary">
                                {t('settings.workspace.api.key.generated-at', {
                                    date: generatedAt ? dayjs(generatedAt).format('YYYY-MM-DD HH:mm') : '-',
                                })}
                            </Typography>
                            {showKey && (
                                <Box mt={1}>
                                    <Typography variant="caption" color="info.dark">
                                        {t('settings.workspace.api.key.cannot-reshow')}
                                    </Typography>
                                </Box>
                            )}
                        </>
                    }
                />
            </Box>
        </Box>
    );
};

// ---------------------------------------------------------------------------
// WorkspaceApiManager
// ---------------------------------------------------------------------------

const WorkspaceApiManager: React.FC<WorkspaceApiManagerProps> = ({
    apiClient,
    loading,
    hasApiAccess,
    apiQuota,
    dailyUsage,
    periodStartDay,
    periodResetsAt,
    usageLoading = false,
    createdKeys,
    rotatedKey,
    creating,
    onCreateApiClient,
    onRotateSuccess,
}) => {
    const { t } = useTranslation();

    const [rotateApiClient, { loading: regenerating }] = useMutation(RotateApiClientDocument, {
        onError: (error) => {
            if (error.message === 'Invalid password.') {
                setError(t('settings.workspace.api.dialog.regenerate.password.error.incorrect'));
                return;
            }
            setError(t('settings.workspace.api.dialog.regenerate.password.error.failed'));
        },
    });

    const UserKeysAlert = () => (
        <Alert severity="info" icon={<InfoOutlined fontSize="small" />} sx={{ mx: 2, mt: 2 }}>
            {t('settings.workspace.api.info-banner')}
        </Alert>
    );

    const [dialogOpen, setDialogOpen] = useState(false);
    const [password, setPassword] = useState('');
    const [rotatingKeySlot, setRotatingKeySlot] = useState<ApiClientKeySlot | null>(null);
    const [error, setError] = useState('');
    const passwordInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (dialogOpen && passwordInputRef.current) {
            passwordInputRef.current.focus();
        }
    }, [dialogOpen]);

    if (!loading && !apiClient) {
        return (
            <Stack gap={3}>
                <Card>
                    <CardHeader
                        title={t('settings.workspace.api.title')}
                        subheader={t('settings.workspace.api.create-app.subtitle')}
                    />
                    <Divider />
                    <UserKeysAlert />
                    {hasApiAccess ? (
                        <CardContent>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    onClick={onCreateApiClient}
                                    aria-label={t('settings.workspace.api.create-app.button')}
                                    loading={creating}
                                >
                                    {t('settings.workspace.api.create-app.button')}
                                </LoadingButton>
                                <Button
                                    component={Link}
                                    href={API_DOCS_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="outlined"
                                    aria-label={t('settings.workspace.api.tools.documentation')}
                                >
                                    {t('settings.workspace.api.tools.documentation')}
                                </Button>
                            </Stack>
                        </CardContent>
                    ) : (
                        <CardContent>
                            <Typography variant="body2" color="textSecondary">
                                {t('public-api.not-available.description')}
                            </Typography>
                            <Stack direction="row" alignItems="center" gap={1} mt={1}>
                                <Button
                                    component={Link}
                                    href={API_DOCS_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="contained"
                                    aria-label={t('settings.workspace.api.tools.documentation')}
                                >
                                    {t('settings.workspace.api.tools.documentation')}
                                </Button>
                                <Button
                                    onClick={() => toggleChat()}
                                    variant="outlined"
                                    aria-label={t('settings.workspace.api.tools.chat')}
                                >
                                    {t('settings.workspace.api.tools.chat')}
                                </Button>
                            </Stack>
                        </CardContent>
                    )}
                </Card>
            </Stack>
        );
    }

    const handleRegenerate = (keySlot: ApiClientKeySlot) => {
        setRotatingKeySlot(keySlot);
        setDialogOpen(true);
        setPassword('');
        setError('');
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setRotatingKeySlot(null);
        setPassword('');
        setError('');
    };

    const handleConfirm = async () => {
        if (!password) {
            setError(t('settings.workspace.api.dialog.regenerate.password.error.required'));
            return;
        }
        if (!rotatingKeySlot) {
            setError(t('settings.workspace.api.dialog.regenerate.keyslot.error.invalid'));
            return;
        }
        try {
            const result = await rotateApiClient({ variables: { keySlot: rotatingKeySlot, password } });
            const rotated = result?.data?.rotateApiClient;
            const newKey =
                rotatingKeySlot === ApiClientKeySlot.Primary ? rotated?.primaryKey : rotated?.secondaryKey;
            const newGeneratedAt =
                rotatingKeySlot === ApiClientKeySlot.Primary
                    ? rotated?.primaryKeyGeneratedAt
                    : rotated?.secondaryKeyGeneratedAt;

            if (newKey) {
                onRotateSuccess(rotatingKeySlot, newKey, newGeneratedAt);
                enqueueSnackbar(t('settings.workspace.api.key.rotated.success'));
                handleDialogClose();
            } else {
                setError(t('settings.workspace.api.dialog.regenerate.password.error.failed'));
            }
        } catch (e) {
            if (e instanceof ApolloError) {
                if (e.message === 'Invalid password.') {
                    setError(t('settings.workspace.api.dialog.regenerate.password.error.incorrect'));
                    return;
                }
            }
            setError(t('settings.workspace.api.dialog.regenerate.password.error.failed'));
        }
    };

    return (
        <>
            <Stack gap={3}>
                <Card>
                    <CardHeader
                        title={t('settings.workspace.api.title')}
                        subheader={t('settings.workspace.api.subtitle')}
                    />
                    <Divider />
                    <UserKeysAlert />
                    <CardContent>
                        <Stack gap={3}>
                            {loading ? (
                                <>
                                    <Box>
                                        <Skeleton variant="text" width={80} height={24} />
                                        <Skeleton variant="rounded" height={28} width="60%" sx={{ mt: 1 }} />
                                    </Box>
                                    <Stack gap={2}>
                                        <Skeleton variant="text" width={120} height={24} />
                                        <Skeleton variant="rounded" height={36} width="80%" />
                                        <Skeleton variant="text" width={120} height={24} sx={{ mt: 2 }} />
                                        <Skeleton variant="rounded" height={36} width="80%" />
                                    </Stack>
                                    <Box>
                                        <Skeleton variant="text" width={80} height={24} />
                                        <Skeleton variant="rounded" height={28} width="40%" sx={{ mt: 1 }} />
                                    </Box>
                                    <Box>
                                        <Skeleton variant="text" width={100} height={32} />
                                        <Stack direction="row" spacing={2} mt={1}>
                                            <Skeleton variant="rounded" width={200} height={36} />
                                            <Skeleton variant="rounded" width={180} height={36} />
                                        </Stack>
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <Stack gap={2} direction={{ xs: 'column', md: 'row' }}>
                                        <Box flex={1}>
                                            <Typography variant="subtitle2">
                                                {t('settings.workspace.api.app-id.label')}
                                            </Typography>
                                            <Stack direction="row" alignItems="center" gap={1}>
                                                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                                    {apiClient?.id || (
                                                        <i>{t('settings.workspace.api.key.not-generated')}</i>
                                                    )}
                                                </Typography>
                                                {apiClient?.id && (
                                                    <Tooltip title={t('str.action.copy')}>
                                                        <IconButton
                                                            onClick={() =>
                                                                copyClipboard(
                                                                    apiClient.id,
                                                                    () => {
                                                                        enqueueSnackbar(
                                                                            t('str.action.copied'),
                                                                        );
                                                                    },
                                                                    () => {
                                                                        enqueueSnackbar(
                                                                            t('error.something-wrong'),
                                                                        );
                                                                    },
                                                                )
                                                            }
                                                            aria-label={t('str.action.copy')}
                                                            tabIndex={0}
                                                            sx={{ minWidth: 0, px: 1 }}
                                                        >
                                                            <ContentCopyRounded fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </Box>
                                        <Stack flex={1}>
                                            <Typography variant="subtitle2">
                                                {t('settings.workspace.api.last-used.label')}
                                            </Typography>
                                            <Stack direction="row" alignItems="center" flexGrow={1} gap={1}>
                                                <Typography variant="body2">
                                                    {apiClient?.lastKeySlotUsed
                                                        ? t('settings.workspace.api.last-used', {
                                                              keyType: t(
                                                                  apiClient.lastKeySlotUsed ===
                                                                      ApiClientKeySlot.Primary
                                                                      ? 'settings.workspace.api.primary'
                                                                      : 'settings.workspace.api.secondary',
                                                              ),
                                                              date: apiClient.lastUsedAt
                                                                  ? dayjs(apiClient.lastUsedAt).format(
                                                                        'YYYY-MM-DD HH:mm',
                                                                    )
                                                                  : '-',
                                                          })
                                                        : t('settings.workspace.api.last-used.never')}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                    <Stack gap={2} direction={{ xs: 'column', md: 'row' }}>
                                        <ApiKeySection
                                            label={'settings.workspace.api.key.primary.label'}
                                            keyValue={
                                                rotatedKey?.slot === ApiClientKeySlot.Primary
                                                    ? rotatedKey.key
                                                    : (apiClient?.primaryKey ??
                                                      createdKeys?.primaryKey ??
                                                      undefined)
                                            }
                                            generatedAt={
                                                rotatedKey?.slot === ApiClientKeySlot.Primary
                                                    ? ((rotatedKey.generatedAt ||
                                                          apiClient?.primaryKeyGeneratedAt) ??
                                                      undefined)
                                                    : (apiClient?.primaryKeyGeneratedAt ?? undefined)
                                            }
                                            keySlot={ApiClientKeySlot.Primary}
                                            onRegenerate={handleRegenerate}
                                        />
                                        <ApiKeySection
                                            label={'settings.workspace.api.key.secondary.label'}
                                            keyValue={
                                                rotatedKey?.slot === ApiClientKeySlot.Secondary
                                                    ? rotatedKey.key
                                                    : (apiClient?.secondaryKey ??
                                                      createdKeys?.secondaryKey ??
                                                      undefined)
                                            }
                                            generatedAt={
                                                rotatedKey?.slot === ApiClientKeySlot.Secondary
                                                    ? ((rotatedKey.generatedAt ||
                                                          apiClient?.secondaryKeyGeneratedAt) ??
                                                      undefined)
                                                    : (apiClient?.secondaryKeyGeneratedAt ?? undefined)
                                            }
                                            keySlot={ApiClientKeySlot.Secondary}
                                            onRegenerate={handleRegenerate}
                                        />
                                    </Stack>

                                    <Box>
                                        <Typography variant="h6">
                                            {t('settings.workspace.api.usage.title')}
                                        </Typography>
                                        <Box mt={1}>
                                            <IntegrationUsageMeter
                                                quota={apiQuota}
                                                periodResetsAt={periodResetsAt}
                                                loading={usageLoading}
                                                labelKey="settings.workspace.api.usage.label"
                                                unavailableKey="public-api.not-available.description"
                                                freeOfferKey="settings.workspace.api.usage.free-offer"
                                                freeOfferTitleKey="settings.workspace.api.usage.free-offer.title"
                                                limitReachedTitleKey="settings.workspace.api.usage.limit-reached.title"
                                                limitReachedKey="settings.workspace.api.usage.limit-reached"
                                                planLimit={PlanLimit.PublicApiRequestsLimit}
                                                dataId="api-usage-meter"
                                            />
                                            {apiQuota?.limit != null && apiQuota.limit > 0 && (
                                                <Box mt={2}>
                                                    <IntegrationUsageChart
                                                        days={dailyUsage}
                                                        kind="api"
                                                        periodStartDay={periodStartDay}
                                                        loading={usageLoading}
                                                        dataId="api-usage-chart"
                                                    />
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Typography variant="h6">
                                            {t('settings.workspace.api.tools.title')}
                                        </Typography>
                                        <Stack direction="row" spacing={2} mt={1}>
                                            <Button
                                                component={Link}
                                                href={API_DOCS_URL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                variant="contained"
                                                aria-label={t('settings.workspace.api.tools.documentation')}
                                            >
                                                {t('settings.workspace.api.tools.documentation')}
                                            </Button>
                                        </Stack>
                                    </Box>
                                </>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>
            <Dialog open={dialogOpen} onClose={handleDialogClose} aria-labelledby="regenerate-dialog-title">
                <DialogTitle id="regenerate-dialog-title">
                    {t('settings.workspace.api.dialog.regenerate.title')}
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>{t('settings.workspace.api.dialog.regenerate.body')}</Typography>
                    <TextField
                        label={t('settings.workspace.api.dialog.regenerate.password.label')}
                        type="password"
                        fullWidth
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleConfirm();
                            }
                        }}
                        inputRef={passwordInputRef}
                        disabled={regenerating}
                        error={!!error}
                        helperText={error}
                        inputProps={{
                            'aria-label': t('settings.workspace.api.dialog.regenerate.password.label'),
                        }}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} disabled={regenerating}>
                        {t('settings.workspace.api.dialog.regenerate.cancel')}
                    </Button>
                    <LoadingButton onClick={handleConfirm} variant="contained" loading={regenerating}>
                        {t('settings.workspace.api.dialog.regenerate.confirm')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default WorkspaceApiManager;
